/**
 * InfoPopover - Reusable info popover component
 *
 * Displays a styled popover with component/parameter information
 */

import { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Typography,
  Chip
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

const InfoPopover = ({
  title,
  description,
  accentColor = '#2563eb',
  tags = [],
  size = 'medium', // 'small' | 'medium'
  iconSx = {}
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const iconSize = size === 'small' ? 16 : 18;

  if (!description) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          color: open ? accentColor : 'rgba(255,255,255,0.35)',
          transition: 'all 0.2s ease',
          '&:hover': {
            color: accentColor,
            backgroundColor: `${accentColor}15`
          },
          p: 0.5,
          ...iconSx
        }}
      >
        <InfoIcon sx={{ fontSize: iconSize }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#12121a',
              backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px',
              maxWidth: 380,
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 0 1px rgba(255,255,255,0.1)',
              mt: 1
            }
          }
        }}
      >
        {/* Header with accent color bar */}
        {title && (
          <Box
            sx={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentColor}99)`,
              px: 2,
              py: 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.05)'
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              {title}
            </Typography>
          </Box>
        )}

        {/* Content */}
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.8125rem',
              lineHeight: 1.7,
              letterSpacing: '0.01em'
            }}
          >
            {description}
          </Typography>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                mt: 2,
                pt: 1.5,
                borderTop: '1px solid rgba(255,255,255,0.06)'
              }}
            >
              {tags.slice(0, 5).map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.6875rem',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.6)',
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default InfoPopover;
